/**
 * Platform Settings → Plans & Entitlements (access control center)
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { usePlatformIntegrationAuth } from '../../hooks/usePlatformIntegrationAuth';
import { IntegrationLoadingSpinner, isIntegrationFetchFailure } from '../../components/platform/IntegrationFetchState';
import {
  Crown,
  Zap,
  Shield,
  Users,
  Building2,
  FileText,
  RefreshCw,
  Database,
  AlertTriangle,
  Check,
  X,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '').replace(/\/api$/, '');

const TABS = [
  { id: 'plans', label: 'All Plans', path: '/platform/settings/plans', icon: Crown },
  { id: 'entitlements', label: 'Entitlements', path: '/platform/settings/plans/entitlements', icon: Zap },
  { id: 'roles', label: 'Role Templates', path: '/platform/settings/plans/role-templates', icon: Shield },
  { id: 'users', label: 'User Access', path: '/platform/settings/plans/user-access', icon: Users },
  { id: 'agencies', label: 'Agency Access', path: '/platform/settings/plans/agency-access', icon: Building2 },
  { id: 'audit', label: 'Overrides & Audit', path: '/platform/settings/plans/audit', icon: FileText },
];

const PLAN_SCOPE_FILTERS = [
  { id: 'all', label: 'All Plans' },
  { id: 'self_repair', label: 'Self-Repair Plans' },
  { id: 'agency', label: 'Agency Plans' },
  { id: 'platform_internal', label: 'Platform Internal' },
];

const USER_TYPE_FILTERS = [
  { id: 'all', label: 'All Users', param: '' },
  { id: 'self_repair', label: 'Self-Repair Users', param: 'self_repair' },
  { id: 'agency', label: 'Agency Users', param: 'agency' },
  { id: 'platform_staff', label: 'Platform Staff', param: 'platform_staff' },
];

const SCOPE_BADGE = {
  self_repair: { label: 'Self-Repair', cls: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  agency: { label: 'Agency', cls: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
  platform_internal: { label: 'Platform Internal', cls: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
};

function tabFromPath(pathname) {
  if (pathname.includes('/entitlements')) return 'entitlements';
  if (pathname.includes('/role-templates')) return 'roles';
  if (pathname.includes('/user-access')) return 'users';
  if (pathname.includes('/agency-access')) return 'agencies';
  if (pathname.includes('/audit')) return 'audit';
  return 'plans';
}

function formatMoney(n) {
  if (n == null) return '—';
  if (n === 0) return '$0';
  return `$${Number(n).toFixed(0)}`;
}

const EndpointBanner = ({ isDark, onRetry, retrying }) => (
  <div className={`flex flex-col sm:flex-row gap-3 p-4 rounded-xl border mb-6 ${isDark ? 'bg-amber-500/10 border-amber-500/30' : 'bg-amber-50 border-amber-200'}`}>
    <div className="flex gap-3 flex-1">
      <AlertTriangle className={`w-5 h-5 shrink-0 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} />
      <p className={`text-sm ${isDark ? 'text-zinc-300' : 'text-stone-700'}`}>
        Access control API unavailable. Deploy backend routes or sign in as platform admin. Persistence is disabled.
      </p>
    </div>
    <Button variant="outline" size="sm" onClick={onRetry} disabled={retrying}>
      <RefreshCw className={`w-4 h-4 mr-2 ${retrying ? 'animate-spin' : ''}`} />
      Retry
    </Button>
  </div>
);

function ScopeBadge({ scope }) {
  const cfg = SCOPE_BADGE[scope] || { label: scope, cls: 'bg-zinc-500/20 text-zinc-400' };
  return <Badge className={`border ${cfg.cls}`}>{cfg.label}</Badge>;
}

function PlanCard({ plan, isDark }) {
  const internal = plan.scope === 'platform_internal' || plan.internal_not_billable;
  const cardCls = isDark ? 'border-zinc-800 bg-zinc-900/50' : 'border-stone-200 bg-white';
  const muted = isDark ? 'text-zinc-400' : 'text-stone-600';
  const textMain = isDark ? 'text-zinc-100' : 'text-stone-900';

  return (
    <Card className={cardCls}>
      <CardHeader className="pb-2">
        <div className="flex justify-between items-start gap-2">
          <div>
            <CardTitle className={`text-lg ${textMain}`}>{plan.plan_name || plan.name}</CardTitle>
            <CardDescription className="font-mono text-xs">{plan.plan_code}</CardDescription>
          </div>
          <ScopeBadge scope={plan.scope} />
        </div>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        <p className={muted}>{plan.description || '—'}</p>
        <div className="flex justify-between">
          <span className={muted}>Price</span>
          <span className={textMain}>
            {internal ? (
              <span className="text-amber-500">Internal — not billable</span>
            ) : (
              <>
                {formatMoney(plan.price_monthly)}/mo · {formatMoney(plan.price_yearly)}/yr
              </>
            )}
          </span>
        </div>
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div>
            <span className={muted}>Credits </span>
            <span className={textMain}>{plan.included_credits ?? '—'}</span>
          </div>
          <div>
            <span className={muted}>Mail </span>
            <span className={textMain}>{plan.monthly_mail_credits ?? '—'}</span>
          </div>
          <div>
            <span className={muted}>AI budget </span>
            <span className={textMain}>{plan.ai_monthly_budget ?? '—'}</span>
          </div>
          <div>
            <span className={muted}>Status </span>
            <span className={textMain}>{plan.status}</span>
          </div>
        </div>
        {plan.scope === 'agency' && (
          <div className={`text-xs pt-1 border-t ${isDark ? 'border-zinc-800' : 'border-stone-200'}`}>
            Seats {plan.max_agency_seats ?? '∞'} · Clients {plan.max_clients ?? '∞'} · Team{' '}
            {plan.max_team_members ?? '∞'}
          </div>
        )}
        <div className="flex gap-2 flex-wrap pt-1">
          {plan.visible_to_users && <Badge variant="outline">Visible to users</Badge>}
          {plan.visible_to_agencies && <Badge variant="outline">Visible to agencies</Badge>}
        </div>
      </CardContent>
    </Card>
  );
}

export default function PlatformPlansPage() {
  const { isDark } = useTheme();
  const { requestConfig, canFetch, authLoading } = usePlatformIntegrationAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const activeTab = useMemo(() => tabFromPath(location.pathname), [location.pathname]);
  const [planScopeFilter, setPlanScopeFilter] = useState('all');
  const [userTypeFilter, setUserTypeFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [tabLoading, setTabLoading] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [summary, setSummary] = useState({ plans: 0, entitlements: 0, role_templates: 0, plans_by_scope: {} });
  const [plans, setPlans] = useState([]);
  const [entitlements, setEntitlements] = useState([]);
  const [roles, setRoles] = useState([]);
  const [accessUsers, setAccessUsers] = useState({ items: [], total: 0 });
  const [accessAgencies, setAccessAgencies] = useState({ items: [], total: 0 });
  const [auditRows, setAuditRows] = useState({ items: [], total: 0 });
  const [userSearch, setUserSearch] = useState('');

  const apiFetch = useCallback(
    async (path, options = {}) => {
      const cfg = requestConfig();
      const res = await fetch(`${API_BASE}/api${path}`, {
        ...options,
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...(cfg.headers || {}), ...(options.headers || {}) },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        const err = new Error(data.detail || data.message || `Request failed (${res.status})`);
        err.status = res.status;
        throw err;
      }
      return res.json();
    },
    [requestConfig]
  );

  const loadSummary = useCallback(async () => {
    try {
      const data = await apiFetch('/platform/settings/plans/summary');
      setSummary({
        plans: data.plans ?? 0,
        entitlements: data.entitlements ?? 0,
        role_templates: data.role_templates ?? 0,
        plans_by_scope: data.plans_by_scope || {},
      });
      setUnavailable(false);
    } catch (err) {
      if (isIntegrationFetchFailure(err)) setUnavailable(true);
    }
  }, [apiFetch]);

  const loadTab = useCallback(async () => {
    if (unavailable) {
      setLoading(false);
      setTabLoading(false);
      return;
    }
    setTabLoading(true);
    try {
      if (activeTab === 'plans') {
        const scopeQ = planScopeFilter !== 'all' ? `&scope=${planScopeFilter}` : '';
        const data = await apiFetch(`/platform/settings/plans?include_hidden=true${scopeQ}`);
        setPlans(data.items || []);
      } else if (activeTab === 'entitlements') {
        const data = await apiFetch('/platform/settings/entitlements');
        setEntitlements(data.items || []);
      } else if (activeTab === 'roles') {
        const data = await apiFetch('/platform/settings/role-templates');
        setRoles(data.items || []);
      } else if (activeTab === 'users') {
        const typeParam = USER_TYPE_FILTERS.find((f) => f.id === userTypeFilter)?.param;
        const typeQ = typeParam ? `&type=${typeParam}` : '';
        const q = userSearch ? `&q=${encodeURIComponent(userSearch)}` : '';
        const data = await apiFetch(`/platform/settings/access/users?page=1&limit=100${typeQ}${q}`);
        setAccessUsers(data);
      } else if (activeTab === 'agencies') {
        const data = await apiFetch('/platform/settings/access/agencies?page=1&limit=100');
        setAccessAgencies(data);
      } else if (activeTab === 'audit') {
        const data = await apiFetch('/platform/settings/access/audit?page=1&limit=100');
        setAuditRows(data);
      }
      setUnavailable(false);
    } catch (err) {
      if (isIntegrationFetchFailure(err)) {
        setUnavailable(true);
      } else {
        toast.error(err.message || 'Failed to load');
      }
    } finally {
      setTabLoading(false);
      setLoading(false);
    }
  }, [activeTab, apiFetch, planScopeFilter, userTypeFilter, userSearch, unavailable]);

  useEffect(() => {
    if (!canFetch) return;
    setLoading(true);
    loadSummary().then(() => loadTab());
  }, [canFetch, activeTab, loadSummary, loadTab]);

  const handleRefresh = async () => {
    setRetrying(true);
    await loadSummary();
    await loadTab();
    setRetrying(false);
  };

  const handleSeed = async () => {
    setSeeding(true);
    try {
      await apiFetch('/platform/settings/plans/seed-defaults', { method: 'POST', body: '{}' });
      toast.success('Defaults seeded');
      await loadSummary();
      await loadTab();
    } catch (err) {
      toast.error(err.message || 'Seed failed');
    } finally {
      setSeeding(false);
    }
  };

  const handleTabChange = (tabId) => {
    const tab = TABS.find((t) => t.id === tabId);
    if (tab) navigate(tab.path);
  };

  const filteredPlans =
    planScopeFilter === 'all' ? plans : plans.filter((p) => p.scope === planScopeFilter);

  if (authLoading) {
    return <IntegrationLoadingSpinner label="Plans & Entitlements" />;
  }

  const cardCls = isDark ? 'border-zinc-800 bg-zinc-900/50' : 'border-stone-200 bg-white';
  const muted = isDark ? 'text-zinc-400' : 'text-stone-600';
  const textMain = isDark ? 'text-zinc-100' : 'text-stone-900';

  return (
    <div className="space-y-6 pb-10" data-testid="platform-plans-page">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div className="flex items-start gap-4">
          <div className={`p-3 rounded-2xl ${isDark ? 'bg-amber-500/15 border border-amber-500/30' : 'bg-amber-50 border border-amber-200'}`}>
            <Crown className="w-7 h-7 text-amber-500" />
          </div>
          <div>
            <h1 className={`text-2xl md:text-3xl font-bold ${textMain}`}>Plans & Entitlements</h1>
            <p className={`mt-1 text-sm max-w-3xl ${muted}`}>
              Platform owner controls the whole SaaS: self-repair pricing, agency pricing, entitlements, roles, and
              overrides. Agencies manage their own users and clients within their plan limits.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={handleRefresh} disabled={retrying} className={isDark ? 'border-zinc-700' : ''}>
            <RefreshCw className={`w-4 h-4 mr-2 ${retrying ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={handleSeed} disabled={seeding || unavailable} className="bg-amber-500 hover:bg-amber-600 text-black">
            <Database className="w-4 h-4 mr-2" />
            Seed Defaults
          </Button>
        </div>
      </div>

      {unavailable && <EndpointBanner isDark={isDark} onRetry={handleRefresh} retrying={retrying} />}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className={cardCls}>
          <CardContent className="p-4">
            <p className={`text-xs ${muted}`}>Total Plans</p>
            <p className={`text-2xl font-bold ${textMain}`}>{summary.plans}</p>
          </CardContent>
        </Card>
        <Card className={cardCls}>
          <CardContent className="p-4">
            <p className={`text-xs ${muted}`}>Self-Repair Plans</p>
            <p className={`text-2xl font-bold ${textMain}`}>{summary.plans_by_scope?.self_repair ?? 0}</p>
          </CardContent>
        </Card>
        <Card className={cardCls}>
          <CardContent className="p-4">
            <p className={`text-xs ${muted}`}>Agency Plans</p>
            <p className={`text-2xl font-bold ${textMain}`}>{summary.plans_by_scope?.agency ?? 0}</p>
          </CardContent>
        </Card>
        <Card className={cardCls}>
          <CardContent className="p-4">
            <p className={`text-xs ${muted}`}>Entitlements / Roles</p>
            <p className={`text-2xl font-bold ${textMain}`}>
              {summary.entitlements} / {summary.role_templates}
            </p>
          </CardContent>
        </Card>
      </div>

      <div className={`flex gap-2 overflow-x-auto p-1 rounded-xl border ${isDark ? 'bg-zinc-900/50 border-zinc-800' : 'bg-stone-100 border-stone-200'}`}>
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const active = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => handleTabChange(tab.id)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${
                active ? (isDark ? 'bg-zinc-800 text-amber-400' : 'bg-white text-amber-700 shadow-sm') : muted
              }`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {tabLoading || (loading && activeTab === 'plans' && plans.length === 0) ? (
        <div className="space-y-3">
          <Skeleton className={`h-40 w-full ${isDark ? 'bg-zinc-800' : ''}`} />
          <Skeleton className={`h-40 w-full ${isDark ? 'bg-zinc-800' : ''}`} />
        </div>
      ) : (
        <>
          {activeTab === 'plans' && (
            <div className="space-y-4">
              <div className={`flex flex-wrap gap-2 p-1 rounded-lg border ${isDark ? 'border-zinc-800' : 'border-stone-200'}`}>
                {PLAN_SCOPE_FILTERS.map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => {
                      setPlanScopeFilter(f.id);
                    }}
                    className={`px-3 py-1.5 rounded-md text-sm ${
                      planScopeFilter === f.id
                        ? isDark
                          ? 'bg-amber-600/30 text-amber-300'
                          : 'bg-amber-100 text-amber-800'
                        : muted
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
              {filteredPlans.length === 0 ? (
                <Card className={cardCls}>
                  <CardContent className="py-12 text-center">
                    <Crown className={`w-12 h-12 mx-auto mb-4 ${muted}`} />
                    <p className={textMain}>No plans in this scope.</p>
                    <Button className="mt-4 bg-amber-500 text-black" onClick={handleSeed} disabled={seeding || unavailable}>
                      Seed Defaults
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {filteredPlans.map((plan) => (
                    <PlanCard key={plan.plan_id} plan={plan} isDark={isDark} />
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'entitlements' && (
            <>
              {entitlements.length === 0 ? (
                <Card className={cardCls}>
                  <CardContent className="py-12 text-center">
                    <p className={muted}>No entitlements. Seed defaults to populate the catalog.</p>
                    <Button className="mt-4 bg-amber-500 text-black" onClick={handleSeed} disabled={unavailable}>
                      Seed Defaults
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {entitlements.map((ent) => (
                    <Card key={ent.entitlement_key} className={cardCls}>
                      <CardContent className="p-4">
                        <div className="flex justify-between">
                          <p className={`font-medium ${textMain}`}>{ent.label || ent.name}</p>
                          {ent.active !== false ? <Check className="w-4 h-4 text-emerald-500" /> : <X className="w-4 h-4 text-red-400" />}
                        </div>
                        <p className="text-xs font-mono text-emerald-500/90 mt-1">{ent.entitlement_key}</p>
                        <div className="flex gap-2 mt-2">
                          <Badge variant="secondary">{ent.category}</Badge>
                          <Badge variant="outline">{ent.type}</Badge>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </>
          )}

          {activeTab === 'roles' && (
            <div className="space-y-3">
              {roles.length === 0 ? (
                <Card className={cardCls}>
                  <CardContent className="py-12 text-center">
                    <p className={muted}>No role templates. Seed defaults.</p>
                    <Button className="mt-4 bg-amber-500 text-black" onClick={handleSeed} disabled={unavailable}>
                      Seed Defaults
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                roles.map((role) => (
                  <Card key={role.role_key} className={cardCls}>
                    <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div>
                        <p className={`font-medium ${textMain}`}>
                          {role.role_name}{' '}
                          {role.full_access && <Badge className="ml-2 bg-amber-500/20 text-amber-400">Full access</Badge>}
                        </p>
                        <p className={`text-xs font-mono ${muted}`}>{role.role_key}</p>
                        <p className={`text-sm mt-1 ${muted}`}>{role.description}</p>
                      </div>
                      <div className="flex gap-2">
                        <ScopeBadge scope={role.scope === 'platform' ? 'platform_internal' : role.scope} />
                        <Badge variant="outline">{(role.permissions || []).length} permissions</Badge>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          )}

          {activeTab === 'users' && (
            <Card className={cardCls}>
              <CardHeader>
                <CardTitle className={textMain}>User Access</CardTitle>
                <CardDescription>
                  Self-repair users use self-repair plans. Agency users inherit agency plans. Platform staff have full
                  access and are not limited by commercial plans.
                </CardDescription>
                <div className={`flex flex-wrap gap-2 mt-3 p-1 rounded-lg border ${isDark ? 'border-zinc-800' : 'border-stone-200'}`}>
                  {USER_TYPE_FILTERS.map((f) => (
                    <button
                      key={f.id}
                      type="button"
                      onClick={() => setUserTypeFilter(f.id)}
                      className={`px-3 py-1.5 rounded-md text-sm ${
                        userTypeFilter === f.id ? (isDark ? 'bg-zinc-800 text-amber-400' : 'bg-amber-100 text-amber-800') : muted
                      }`}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
                <div className="flex gap-2 pt-2 max-w-md">
                  <Input
                    placeholder="Search email or name"
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    className={isDark ? 'bg-zinc-950 border-zinc-700' : ''}
                  />
                  <Button variant="outline" onClick={loadTab} disabled={unavailable}>
                    Search
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="overflow-x-auto">
                {(accessUsers.items || []).length === 0 ? (
                  <p className={muted}>No users match this filter.</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>User</TableHead>
                        <TableHead>Access type</TableHead>
                        <TableHead>Agency</TableHead>
                        <TableHead>Plan</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Credits</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {accessUsers.items.map((row) => (
                        <TableRow key={row.user_id}>
                          <TableCell>
                            <div className={`font-medium ${textMain}`}>{row.user_name || row.email}</div>
                            <div className={`text-xs ${muted}`}>{row.email}</div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{row.access_type?.replace('_', ' ')}</Badge>
                          </TableCell>
                          <TableCell>{row.agency_name || '—'}</TableCell>
                          <TableCell>{row.current_plan || '—'}</TableCell>
                          <TableCell>{row.role_template || '—'}</TableCell>
                          <TableCell>{row.account_status}</TableCell>
                          <TableCell>{row.credits ?? 0}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          )}

          {activeTab === 'agencies' && (
            <Card className={cardCls}>
              <CardHeader>
                <CardTitle className={textMain}>Agency Access</CardTitle>
                <CardDescription>
                  Control agency plans, seats, clients, mail credits, and white-label. Agency owners manage their own
                  team and clients only.
                </CardDescription>
              </CardHeader>
              <CardContent className="overflow-x-auto">
                {(accessAgencies.items || []).length === 0 ? (
                  <p className={muted}>No agencies found.</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Agency</TableHead>
                        <TableHead>Agency plan</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Seats</TableHead>
                        <TableHead>Clients</TableHead>
                        <TableHead>Mail credits</TableHead>
                        <TableHead>White label</TableHead>
                        <TableHead>Domain</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {accessAgencies.items.map((row) => (
                        <TableRow key={row.agency_id}>
                          <TableCell className={textMain}>{row.agency_name || row.agency_id}</TableCell>
                          <TableCell>{row.agency_plan || row.plan || '—'}</TableCell>
                          <TableCell>{row.status}</TableCell>
                          <TableCell>
                            {row.seats_used ?? 0}
                            {row.seat_limit != null ? ` / ${row.seat_limit}` : ''}
                          </TableCell>
                          <TableCell>
                            {row.clients_count ?? 0}
                            {row.client_limit != null ? ` / ${row.client_limit}` : ''}
                          </TableCell>
                          <TableCell>{row.mail_credits ?? 0}</TableCell>
                          <TableCell>{row.white_label_enabled ? 'Yes' : 'No'}</TableCell>
                          <TableCell>{row.custom_domain_status || '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          )}

          {activeTab === 'audit' && (
            <Card className={cardCls}>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className={textMain}>Overrides & Audit</CardTitle>
                  <CardDescription>Plan, role, status, and entitlement override changes.</CardDescription>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={unavailable}
                  onClick={async () => {
                    try {
                      const cfg = requestConfig();
                      const res = await fetch(`${API_BASE}/api/platform/settings/access/audit/export`, {
                        credentials: 'include',
                        headers: cfg.headers || {},
                      });
                      if (!res.ok) throw new Error('Export failed');
                      const blob = await res.blob();
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = 'access-audit.csv';
                      a.click();
                      URL.revokeObjectURL(url);
                    } catch (e) {
                      toast.error(e.message);
                    }
                  }}
                >
                  Export CSV
                </Button>
              </CardHeader>
              <CardContent className="overflow-x-auto">
                {(auditRows.items || []).length === 0 ? (
                  <p className={muted}>No audit entries yet.</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>When</TableHead>
                        <TableHead>Target</TableHead>
                        <TableHead>Action</TableHead>
                        <TableHead>Change</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {auditRows.items.map((row, i) => (
                        <TableRow key={i}>
                          <TableCell className="text-xs whitespace-nowrap">
                            {row.changed_at ? new Date(row.changed_at).toLocaleString() : '—'}
                          </TableCell>
                          <TableCell>
                            {row.override_type}: {row.target}
                          </TableCell>
                          <TableCell>{row.action}</TableCell>
                          <TableCell className="text-xs">
                            {String(row.old_value)} → {String(row.new_value)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
